// Course Package Import — question parsing + normalisation (Quiz & Assignment).
// RFC 4180 CSV parser (no new dependency) + normaliser to the exact shape the
// Course Builder editor and learner renderers read (PRD section 6).

export const ALL_QUESTION_TYPES = [
  'multiple-choice', 'true-false', 'short-answer', 'essay', 'code', 'url', 'video-link', 'file-upload',
];
export const QUIZ_QUESTION_TYPES = ['multiple-choice', 'true-false', 'short-answer', 'essay', 'code'];
export const CODE_LANGUAGES = ['python', 'javascript', 'java', 'cpp', 'c', 'sql', 'html', 'css', 'bash', 'json'];

// RFC 4180 CSV -> array of string[] rows. Handles quoted fields with commas
// and newlines, and "" escaped quotes.
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const s = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field); field = '';
    } else if (c === '\n') {
      row.push(field); field = '';
      rows.push(row); row = [];
    } else {
      field += c;
    }
  }
  // flush last field/row (unless the text ended on a trailing newline)
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // drop fully-empty trailing rows
  return rows.filter((r) => !(r.length === 1 && r[0].trim() === ''));
}

// Normalise one raw question object into the stored shape, or return { error }.
// `resolveCode(ref)` optionally resolves a {file:"..."} code reference to text;
// if it returns undefined the reference is kept as-is (validation resolves it).
export function normaliseQuestion(raw, { allowedTypes = ALL_QUESTION_TYPES, resolveCode } = {}) {
  if (!raw || typeof raw !== 'object') return { error: 'not an object' };
  const type = String(raw.type || '').trim();
  const question = String(raw.question || '').trim();
  if (!question) return { error: 'missing question text' };
  if (!ALL_QUESTION_TYPES.includes(type)) return { error: `unknown type "${type}"` };
  if (!allowedTypes.includes(type)) return { error: `type "${type}" not allowed here` };

  let points = Number(raw.points);
  if (!Number.isFinite(points) || points < 0) points = 1;

  const out = { question, type, points };

  if (type === 'multiple-choice') {
    const options = (Array.isArray(raw.options) ? raw.options : [])
      .map((o) => String(o).trim()).filter(Boolean);
    if (options.length < 2 || options.length > 6) return { error: 'multiple-choice needs 2–6 options' };
    const answer = String(raw.answer || '').trim();
    if (!options.includes(answer)) return { error: 'answer is not one of the options' };
    out.options = options;
    out.answer = answer;
  } else if (type === 'true-false') {
    const a = String(raw.answer || '').trim().toLowerCase();
    if (a !== 'true' && a !== 'false') return { error: 'true-false answer must be True or False' };
    out.options = ['True', 'False'];
    out.answer = a === 'true' ? 'True' : 'False';
  } else if (type === 'short-answer') {
    out.answer = String(raw.answer || '').trim();
  } else if (type === 'essay') {
    if (raw.rubric) out.rubric = String(raw.rubric);
  } else if (type === 'code') {
    const cd = raw.codeDetails || {};
    const language = String(cd.language || '').trim().toLowerCase();
    if (!CODE_LANGUAGES.includes(language)) {
      return { error: `code language must be one of ${CODE_LANGUAGES.join(', ')}` };
    }
    let correctCode = cd.correctCode;
    if (correctCode && typeof correctCode === 'object' && correctCode.file) {
      const resolved = resolveCode ? resolveCode(correctCode.file) : undefined;
      if (resolved === undefined) return { error: `code solution file not found: ${correctCode.file}` };
      correctCode = resolved;
    }
    out.codeDetails = { language, correctCode: String(correctCode || '') };
  } else {
    // url, video-link, file-upload
    out.criterion = String(raw.criterion || '').trim();
  }
  return { question: out };
}

// Parse a CSV questions file into raw question objects (pre-normalisation).
// Returns { raw: object[], error? }. `grading_hint` maps to `criterion`,
// `correct_code` of the form "file:questions/x.py" becomes {file:"..."}.
export function csvToRawQuestions(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return { raw: [], error: 'CSV has no data rows' };
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idx = (name) => header.indexOf(name);
  const col = (r, name) => { const i = idx(name); return i >= 0 ? (r[i] ?? '') : ''; };
  const raw = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const type = col(r, 'type').trim();
    const q = {
      type,
      question: col(r, 'question'),
      points: Number(col(r, 'points')) || 1,
    };
    if (type === 'multiple-choice') {
      q.options = ['option_a', 'option_b', 'option_c', 'option_d', 'option_e', 'option_f']
        .map((k) => col(r, k).trim()).filter(Boolean);
      q.answer = col(r, 'answer');
    } else if (type === 'true-false' || type === 'short-answer') {
      q.answer = col(r, 'answer');
    } else if (type === 'essay') {
      q.rubric = col(r, 'rubric');
    } else if (type === 'code') {
      let cc = col(r, 'correct_code');
      if (/^file:/i.test(cc)) cc = { file: cc.replace(/^file:/i, '').trim() };
      q.codeDetails = { language: col(r, 'language'), correctCode: cc };
    } else {
      q.criterion = col(r, 'grading_hint');
    }
    raw.push(q);
  }
  return { raw };
}
